import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useReactToPrint } from "react-to-print";
import Barcode from "react-barcode";
import { getInvoiceById } from "../../../store/slices/invoiceSlice";
import s from "./InvoicereportPdf.module.scss";
import qrcodeImg from "../../../images/qr.png";
 
const InvoicePDF = () => {
  const dispatch = useDispatch();
  const componentRef = useRef();
 
  // --- REDUX DATA ---
  const { clinicInfo } = useSelector((state) => state.clinic);
  const clinic = clinicInfo && clinicInfo?.length > 0 ? clinicInfo[0] : [];
  const { invoiceInFo } = useSelector((state) => state.invoice);
  const { token } = useSelector((state) => state.auth);
  const invoiceId = sessionStorage.getItem("invoiceId");
 
  // --- FETCH DATA ---
  useEffect(() => {
    if (invoiceId && token) {
      dispatch(getInvoiceById({ id: invoiceId, token }));
    }
  }, [dispatch, invoiceId, token]);
 
  const [pdfHeader, setPdfHeader] = useState(null);
  const [isoLogo, setIsoLogo] = useState(null);
  // --- DYNAMIC CLINIC ADDRESS ---
  const dynamicClinicAddress = `
    ${clinic?.clinicAddress?.plot ? `${clinic.clinicAddress.plot}, ` : ""}
    ${clinic?.clinicAddress?.area ? `${clinic.clinicAddress.area}, ` : ""}
    ${clinic?.clinicAddress?.street ? `${clinic.clinicAddress.street}, ` : ""}
    ${clinic?.clinicAddress?.landMark ? `${clinic.clinicAddress.landMark}, ` : ""}
    ${clinic?.clinicAddress?.city?.name ? `${clinic.clinicAddress.city.name}, ` : ""}
    ${clinic?.clinicAddress?.state?.name ? `${clinic.clinicAddress.state.name}, ` : ""}
    ${clinic?.clinicAddress?.country?.name ? `${clinic.clinicAddress.country.name} ` : ""}
    ${clinic?.clinicAddress?.pin ? clinic.clinicAddress.pin : ""}
  `.trim();
 
  const toBase64 = async (url) => {
    if (!url) return null;
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return null;
    }
  };
 
  useEffect(() => {
    const loadImages = async () => {
      if (clinic?.hospitalInfo?.pdfHeader)
        setPdfHeader(await toBase64(clinic.hospitalInfo.pdfHeader));
      if (clinic?.hospitalInfo?.ISOLogo)
        setIsoLogo(await toBase64(clinic.hospitalInfo.ISOLogo));
    };
    loadImages();
  }, [clinic]);
 
  // --- DYNAMIC BILLING LOGIC ---
  const appointment = invoiceInFo?.appointmentDetails || {};
  const physicianName = invoiceInFo?.appointment?.physicianId?.userId?.name;
  let rows = [];
 
  const getTax = (obj) => {
    if (!obj) return 0;
    if (typeof obj?.tax === "number") return obj.tax;
    for (const key of Object.keys(obj)) {
      if (obj[key] && typeof obj[key] === "object" && "tax" in obj[key])
        return Number(obj[key].tax) || 0;
    }
    return 0;
  };
 
  const addRow = (name, rate, tax = 0, isConsultation = false) => {
    if (!name) return;
    const r = Number(rate || 0);
    const t = isConsultation ? 0 : Number(tax || 0);
    const taxaAmount = (r * t) / 100;
    // const total = r + (r * t) / 100;
    const total = r + taxaAmount;
    const sgst = t / 2;
    const cgst = t / 2
 
    rows.push({ name, rate: r, tax: t, total, sgst, cgst, isConsultation });
  };
 
  if (appointment) {
    // Tests
    (appointment.tests || []).forEach((it) =>
      addRow(
        it?.testId?.testName,
        it?.price || it.totalAmount,
        getTax(it?.testId || it),
      ),
    );
 
    // Profiles
    (appointment.profiles || []).forEach((it) =>
      addRow(
        it?.profileId?.profileName,
        it?.price || it.totalAmount,
        getTax(it?.profileId || it),
      ),
    );
 
    // Consultation Details (Checking if object or array)
    const consults = appointment.consultationDetails;
    const displayName = physicianName ? `Dr. ${physicianName}` : "Consultation";
 
    if (Array.isArray(consults)) {
      consults.forEach((it) => addRow(displayName, it?.price, getTax(it)));
    } else if (consults && typeof consults === "object") {
      addRow(
        displayName,
        consults?.price || invoiceInFo?.totalAmount,
        getTax(consults),
      );
    }
 
    // Radio Services
    (appointment.radioServices || []).forEach((it) => {
      it?.radiologyServiceInfo?.serviceDetails?.forEach((det) =>
        addRow(
          det?.radioId?.serviceDetailsName || det?.serviceName,
          det?.price,
          getTax(det?.radioId),
        ),
      );
    });
 
    // Physiotherapy
    (appointment.physiotherapyService || []).forEach((it) =>
      addRow(it?.physioId?.serviceDetailName, it?.price, getTax(it)),
    );
 
    // Health Screening
    (appointment.healthScreeningDetails || []).forEach((it) =>
      addRow(it?.healthScreeningName, it?.healthScreeningAmount, getTax(it)),
    );
 
    // Health Packages
    (appointment.healthPackageDetails || []).forEach((it) =>
      addRow(it?.healthPackageName, it?.healthPackageAmount, getTax(it)),
    );
    // Dental Services
    (appointment.dentalServices || []).forEach((it) =>
      addRow(
        it?.dentalId?.serviceName,
        it?.price || it?.dentalId?.pt_price,
        getTax(it?.dentalId)
      )
    );
    //Skin Service
    (appointment.skinServices || []).forEach((it) =>
      addRow(
        it?.skinId?.serviceName,
        it?.price || it?.skinId?.pt_price,
        getTax(it?.skinId)
      )
    );
    //Hair Service
        (appointment.hairServices || []).forEach((it) =>
      addRow(
        it?.hairId?.serviceName,
        it?.price || it?.hairId?.pt_price,
        getTax(it?.hairId)
      )
    );
    //General Service
    (appointment.generalServices || []).forEach((it) =>
      addRow(
        it?.generalId?.serviceName,
        it?.price || it?.generalId?.pt_price,
        getTax(it?.generalId)
      )
    );
  }
 
  const grossTotal = rows.reduce((acc, curr) => acc + curr.total, 0);
  const discount = Number(invoiceInFo?.receiptId?.discountPrice) || 0;
  const netPayable = grossTotal - discount;
  const paidAmount = (invoiceInFo?.receiptId?.paymentDetails || []).reduce(
    (acc, curr) => acc + (Number(curr.paidAmount) || 0),
    0,
  );
  // const balanceDue = grossTotal - discount - paidAmount;
  const balanceDue = netPayable - paidAmount;
 
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Invoice_${invoiceInFo?.invoiceId || "bill"}`,
  });
 
  if (!invoiceInFo)
    return <div className="p-5 text-center">Loading Data...</div>;
 
  return (
    <div className={s.mainWrapper}>
      <div className={`${s.actionBar} no-print`}>
        <div className={s.pageCount}>
          Invoice: <strong>{invoiceInFo?.invoiceId}</strong>
        </div>
        <button className={s.btnDownload} onClick={handlePrint}>
          Download Invoice
        </button>
      </div>
 
      <div ref={componentRef} className={s.reportPage}>
        <div className={s.headerSection}>
          <div className={s.headerLeft}>
            {pdfHeader && (
              <img src={pdfHeader} alt="Logo" className={s.labLogo} />
            )}
            <div className={s.addressText}>
              {/* Dynamic Clinic Name */}
              <strong>{clinic?.clinicName || "Clinic Name"}</strong> <br />
              {/* Dynamic Address String */}
              {dynamicClinicAddress || "Address not available"} <br />
              {/* Dynamic Website & Contact */}
              {clinic?.hospitalInfo?.web_address && (
                <>
                  www.{clinic.hospitalInfo.web_address.replace("www.", "")}{" "}
                  <br />
                </>
              )}
              Contact: {clinic?.phone || "N/A"}
              {clinic?.accountInfo?.gst &&
                ` | GSTIN: ${clinic.accountInfo.gst}`}
            </div>
          </div>
 
          <div className={s.headerRight}>
            <div className={s.trustedWrapper}>
              {isoLogo && (
                <img src={isoLogo} alt="Logo" className={s.trustedLogo} />
              )}
              <div className={s.trustedText}>
                <p className={s.trustedTitle}>Most Trusted Brand</p>
                <div className={s.statBlock}>
                  <span className={s.trustedBold}>40 lakhs+</span>
                  <span className={s.subText}>satisfied customers</span>
                </div>
                <div className={s.statBlock}>
                  <span className={s.trustedBold}>1 crore+</span>
                  <span className={s.subText}>lab tests booked</span>
                </div>
              </div>
            </div>
          </div>
        </div>
 
        <div className={s.blackline}></div>
 
        <div className={s.barcodeRow}>
          <Barcode
            value={invoiceInFo?.invoiceId || "0000"}
            height={28}
            width={1.4}
            fontSize={10}
            displayValue={false}
          />
        </div>
 
        <div className={s.patientDetailsGrid}>
          <table className={s.patientTable}>
            <tbody>
              <tr>
                <td className={s.label}>Name</td>
                <td>: {appointment?.patientName || "N/A"}</td>
                <td className={s.label}>Invoice ID</td>
                <td>: {invoiceInFo?.invoiceId || "N/A"}</td>
              </tr>
              <tr>
                <td className={s.label}>Age/Gender</td>
                <td>
                  : {appointment?.patientId?.userId?.age?.year || "-"} Yrs /{" "}
                  {appointment?.patientId?.userId?.gender || "-"}
                </td>
                <td className={s.label}>Invoice Date</td>
                <td>
                  :{" "}
                  {invoiceInFo?.date
                    ? new Date(invoiceInFo.date).toLocaleDateString("en-GB")
                    : "N/A"}
                </td>
              </tr>
              <tr>
                <td className={s.label}>UHID Number</td>
                <td>: {appointment?.patientId?.UHID || "N/A"}</td>
                <td className={s.label}>Payment Date</td>
                <td>
                  :{" "}
                  {invoiceInFo?.receiptId?.paymentDetails?.[0]?.paymentDate
                    ? new Date(
                      invoiceInFo.receiptId.paymentDetails[0].paymentDate,
                    ).toLocaleDateString("en-GB")
                    : "N/A"}
                </td>
              </tr>
              <tr>
                <td className={s.label}>Referred By</td>
                <td>: {invoiceInFo?.referredBy || "Self"}</td>
                <td className={s.label}>Billed By</td>
                <td>: {clinic?.clinicName || "SimpleeKare Health"}</td>
              </tr>
            </tbody>
          </table>
        </div>
 
        <div className={s.testCategoryHeader}>TAX INVOICE SUMMARY</div>
        <table className={s.resultsTable}>
          <thead>
            <tr style={{ borderBottom: "2px solid #000" }}>
              <th style={{ width: "5%" }}>SL</th>
              <th style={{ width: "10%" }}>Item Name</th>
              <th style={{ width: "10%", textAlign: "left" }}>Service</th>
              <th style={{ width: "10%", textAlign: "left" }}>Rate</th>
              <th style={{ width: "10%", textAlign: "center" }}>Tax %</th>
              <th style={{ width: "10%", textAlign: "center" }}>SGST %</th>
              <th style={{ width: "10%", textAlign: "center" }}>CGST %</th>
              <th style={{ width: "9%", textAlign: "left" }}>Amount(Rs)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>
                  <strong>{row.name}</strong>
                </td>
                <td> {appointment?.service?.serviceName || "N/A"} </td>
                {/* changes */}
                <td style={{ textAlign: "left" }}>
                  {row.rate.toFixed(2)}
                </td>
                <td style={{ textAlign: "center" }}>{row.tax.toFixed(2)}%</td>
                <td style={{ textAlign: "center" }}>
                  {row.sgst.toFixed(2)}%
                </td>
                <td style={{ textAlign: "center" }}>
                  {row.cgst.toFixed(2)}%
                </td>
 
                <td style={{ textAlign: "left" }}>{row.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
 
        {/* --- TOTALS BOX --- */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
          }}
        >
          <div style={{ width: "280px", padding: "10px" }}>
            {/* Gross Amount */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                marginBottom: "5px",
              }}
            >
              <span>Gross Amount:</span>
              <strong>{grossTotal.toFixed(2)}</strong>
            </div>
 
            {/* Discount */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                marginBottom: "5px",
              }}
            >
              <span>Discount:</span>
              <strong style={{ color: "red" }}>- {discount.toFixed(2)}</strong>
            </div>
 
            {/* Net Payable (The total after discount) */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "12px",
                marginBottom: "5px",
                paddingTop: "5px",
                borderTop: "1px dashed #ccc",
              }}
            >
              <span>
                <strong>Net Payable:</strong>
              </span>
              <strong>{netPayable.toFixed(2)}</strong>
            </div>
 
            {/* Amount Paid */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                marginBottom: "5px",
              }}
            >
              <span>Amount Paid:</span>
              <strong style={{ color: "#1bc38b" }}>
                {paidAmount.toFixed(2)}
              </strong>
            </div>
 
            {/* Balance Due */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #000",
                marginTop: "5px",
                paddingTop: "5px",
                fontSize: "13px",
              }}
            >
              <strong>Balance Due:</strong>
              <strong style={{ color: balanceDue > 0 ? "red" : "#1bc38b" }}>
                {balanceDue.toFixed(2)}
              </strong>
            </div>
          </div>
        </div>
 
        <div className={s.reportFooter}>
          <div className={s.footerLeft}>
            <img src={qrcodeImg} alt="QR" className={s.footerQr} />
          </div>
          <div className={s.footerCenter}>
            <p className={s.billedByText}>
              Billed By : {clinic?.clinicName || "SimpleeKare Health"}
            </p>            
          </div>
          <div className={s.footerRight}>
            <div className={s.signatureArea}>
              {clinic?.hospitalInfo?.authorizedSignature && (
                <img
                  src={clinic.hospitalInfo.authorizedSignature}
                  alt="Sign"
                  className={s.signImg}
                />
              )}
              <p className={s.signName}>Authorised Signatory</p>
              <p className={s.signTitle}>
                {clinic?.clinicName || "SimpleeKare Health"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default InvoicePDF;